import * as React from 'react'

interface CanvasProps {
  /**
   * Required: pass a function to draw on the `canvas.context` every `frameCount` frames.
   *
   * It's possible for the context to be `null` if the canvas is already initialized or
   * you're trying to draw in 3D on a 2D canvas. It's very very rare, though.
   */
  draw: (context: CanvasRenderingContext2D | null, frameCount: number) => void
  /**
   * Optional: set any CSS property you want on the canvas.
   */
  style?: React.CSSProperties
}

/**
 * A vanilla `<canvas>` element you can `draw()` on
 */
const Canvas = (props: CanvasProps) => {
  const { draw, ...rest } = props
  const canvasRef = React.useRef(null)

  React.useEffect(() => {
    const canvas = canvasRef.current! as HTMLCanvasElement
    const context = canvas!.getContext('2d')
    let frameCount = 0
    let animationFrameId: number

    // main loop: runs your provided draw() function
    const render = () => {
      frameCount++
      resizeCanvasToDisplaySize(canvas)
      draw(context, frameCount)
      animationFrameId = window.requestAnimationFrame(render)
    }

    render()

    return () => {
      window.cancelAnimationFrame(animationFrameId)
    }
  }, [draw])

  /**
   * Always resize canvas to cover all of the container
   */
  function resizeCanvasToDisplaySize(canvas: HTMLCanvasElement) {
    const { width, height } = canvas.getBoundingClientRect()

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
      return true
    }

    return false
  }

  return <canvas ref={canvasRef} {...rest} style={props.style} />
}

export default Canvas
